<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
    title="パケットフィルタ設定"
  >
    <template v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid>
            <v-row class="mb-3">
              <v-col cols="6">
                <v-row v-if="wnumberAct">
                  <v-col cols="12">
                    <app-label-group label="VNコネクト名">
                      <template #value>
                        <div class="ml-3">
                          {{ vnConnectNameAct }}({{ wnumberAct }})
                          <div v-if="wnumberSby">
                            {{ vnConnectNameSby }}({{ wnumberSby }})
                          </div>
                        </div>
                      </template>
                    </app-label-group>
                  </v-col>
                </v-row>
                <v-row v-if="enumber">
                  <v-col cols="5">
                    <app-label-group label="回線番号(E番号)" :value="enumber" />
                  </v-col>
                  <v-col cols="5">
                    <app-label-group
                      label="メニュー"
                      :value="$filter.enumTo(menu, 'acLinePktFilterMenu')"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6" class="d-flex flex-row ga-2 justify-end">
                <v-btn color="primary" @click="downLoadConfig"
                  >設定ダウンロード</v-btn
                >
                <v-btn color="primary" @click="upLoadConfig"
                  >設定アップロード</v-btn
                >
              </v-col>
            </v-row>
            <v-row class="mb-2 mr-0">
              <v-col cols="12" class="d-flex flex-row ga-2 justify-end">
                <v-btn
                  color="primary"
                  @click="registerPacket"
                  density="comfortable"
                  >ルール追加</v-btn
                >
                <v-btn
                  color="primary"
                  @click="deleteUserRules"
                  density="comfortable"
                  >ルール全削除</v-btn
                >
              </v-col>
            </v-row>
            <app-draggable-table
              v-model="rules.items"
              density="compact"
              primary-key="_index"
              :fields="rules.fields"
              :items="rules.items"
              :hover="true"
              :fixed="true"
              @click:row="editPacket"
              :td-class="cellClass"
              :tr-class="rowClass"
            >
              <template #cell(protocol)="data">
                <template v-if="data.item.protocol">
                  {{ $filter.enumTo(data.item.protocol, "protocol") }}
                </template>
                <template v-else>
                  {{ $filter.emptyTo(data.item.protocolNumber, "-") }}
                </template>
              </template>
              <template #cell(action)="data">
                {{ $filter.enumTo(data.item.action, "action") }}
              </template>
              <template #cell(srcAddress)="data">
                {{ $filter.emptyTo(data.item.srcAddress, "any") }}
              </template>
              <template #cell(srcPort)="data">
                {{ $filter.emptyTo(data.item.srcPort, "any") }}
              </template>
              <template #cell(dstAddress)="data">
                {{ $filter.emptyTo(data.item.dstAddress, "any") }}
              </template>
              <template #cell(dstPort)="data">
                {{ $filter.emptyTo(data.item.dstPort, "any") }}
              </template>
              <template #cell(description)="data">
                {{ $filter.emptyTo(data.item.description, "-") }}
              </template>
              <template #cell(filteringStatus)="data">
                {{
                  $filter.enumTo(data.item.filteringStatus, "filteringStatus")
                }}
              </template>
              <template #cell(delete)="{ item }">
                <v-btn
                  density="comfortable"
                  v-if="!item.isDefaultRule"
                  variant="flat"
                  color="primary"
                  @click.stop="deleteRow(item)"
                  >削除
                </v-btn>
              </template>
            </app-draggable-table>
          </v-container>
        </div>
        <v-alert v-if="uploadAlert" class="mb-4" :text="uploadAlert" />
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="modifyPacket" variant="flat" color="primary"
              >設定反映</v-btn
            >
            <v-btn @click="cancel" variant="outlined" color="primary"
              >キャンセル</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { PacketFilterEntity } from "@/apis/PacketFilteringApi";
import cloneDeep from "lodash/cloneDeep";
import { VueFinalModal } from "vue-final-modal";
import type { TableItem } from "@/components/AppTable.vue";
import AppDraggableTable from "@/components/AppDraggableTable.vue";
import type { AppDraggableTableHeader } from "@/components/AppDraggableTable.vue";
import type { UploadError } from "@/services/ConfigDownloadService";
import PacketFilteringRuleEdit from "@/modals/packetFiltering/PacketFilteringRuleEdit.vue";

export default defineComponent({
  name: "PacketFilteringEdit",
  components: {
    AppDraggableTable,
  },
  props: {
    /** パケットフィルタリング設定情報 */
    packets: {
      type: Array as PropType<PacketFilterEntity[]>,
      required: true,
    },
    /** W番号ACT（VNコネクトのパケットフィルタ設定のみ必須）*/
    wnumberAct: {
      type: String as PropType<string>,
      required: false,
    },
    /** VNコネクト名ACT（VNコネクトのパケットフィルタ設定のみ必須）*/
    vnConnectNameAct: {
      type: String as PropType<string>,
      required: false,
    },
    /** W番号SBY（VNコネクトのパケットフィルタ設定のみ必須）*/
    wnumberSby: {
      type: String as PropType<string>,
      required: false,
    },
    /** VNコネクト名SBY（VNコネクトのパケットフィルタ設定のみ必須）*/
    vnConnectNameSby: {
      type: String as PropType<string>,
      required: false,
    },
    /** 排他パラメータ（VNコネクトのパケットフィルタ設定のみ必須）*/
    vnConnectPacketFilterKeyId: {
      type: String as PropType<string>,
      required: false,
    },
    /** E番号（アクセス回線のパケットフィルタ設定のみ必須）*/
    enumber: {
      type: String as PropType<string>,
      required: false,
    },
    /** メニュー（アクセス回線のパケットフィルタ設定のみ必須）*/
    menu: {
      type: String as PropType<string>,
      required: true,
    },
    /** 排他パラメータ（アクセス回線のパケットフィルタ設定のみ必須）*/
    accessLinePacketFilterKeyId: {
      type: String as PropType<string>,
      required: false,
    },
    /** 排他パラメータ（SASEゲートウェイのパケットフィルタ設定のみ必須）*/
    saseGatewayPacketFilterKeyId: {
      type: String as PropType<string>,
      required: false,
    },
  },
  data() {
    const packFilterClone: PacketFilterEntity[] = cloneDeep(this.packets);
    // 主キー相当の項目が無いため、こちらでインデックスを作成
    packFilterClone?.numbering();
    return {
      uploadAlert: null as string | null,
      rules: {
        fields: [
          { key: "_handle", title: "↑↓", width: "50px", align: "center" },
          {
            key: "protocol",
            title: "プロトコル",
            width: "120px",
          },
          {
            key: "action",
            title: "アクション",
            width: "120px",
          },
          {
            key: "srcAddress",
            title: "送信元アドレス",
            width: "160px",
          },
          {
            key: "srcPort",
            title: "送信元ポート",
            width: "120px",
          },
          {
            key: "dstAddress",
            title: "宛先アドレス",
            width: "160px",
          },
          {
            key: "dstPort",
            title: "宛先ポート",
            width: "120px",
          },
          {
            key: "filteringStatus",
            title: "フィルタリング 有効/無効",
            width: "120px",
          },
          {
            key: "description",
            title: "備考",
            width: "200px",
          },
          {
            key: "delete",
            title: "",
            width: "120px",
          },
        ] as AppDraggableTableHeader<TableItem>[],
        items: packFilterClone,
      },
    };
  },
  methods: {
    /** 設定ダウンロード処理 */
    downLoadConfig() {
      const data = this.rules.items.filter((e) => !e.isDefaultRule);
      this.$service.configDownload.downloadPacketFilter(
        this.menu,
        this.wnumberAct,
        this.enumber,
        data
      );
    },

    /** 設定アップロード処理 */
    async upLoadConfig() {
      try {
        const uploadRules =
          await this.$service.configDownload.uploadPacketFilter(
            this.wnumberAct,
            this.enumber
          );
        this.uploadAlert = null;
        await this.$confirm(
          `パケットフィルタリング設定を上書きします。よろしいですか？`
        );

        /** 既存のユーザルールを削除 */
        this.rules.items = this.rules.items.filter((e) => e.isDefaultRule);
        /** アップロードされデータの追加 */
        let index = this.rules.items.length - 1;
        uploadRules.forEach((e: PacketFilterEntity) => {
          this.rules.items.splice(
            index++,
            0,
            this.rules.items.nextNumber({
              ...e,
              protocol: e.protocol ?? "ANY",
              action: e.action ?? "PERMIT",
              filteringStatus: e.filteringStatus ?? "DISABLED",
              isDefaultRule: false,
            })
          );
        });
      } catch (e) {
        this.uploadAlert = (e as UploadError).message;
      }
    },

    /** ルール追加 */
    async registerPacket() {
      this.uploadAlert = null;
      const add: PacketFilterEntity = await this.$modal.show(
        PacketFilteringRuleEdit
      );
      const lastNotDefaultRule = this.rules.items
        .map((e) => e.isDefaultRule)
        .lastIndexOf(false);
      if (lastNotDefaultRule === -1) {
        // ユーザー作成のルールが存在しない場合は最後の暗黙ルールの一つ前にルールを追加
        this.rules.items.splice(
          this.rules.items.length - 1,
          0,
          this.rules.items.nextNumber({ ...add })
        );
      } else {
        // ユーザー作成のルールが存在する場合はその次にルールを追加
        this.rules.items.splice(
          lastNotDefaultRule + 1,
          0,
          this.rules.items.nextNumber({ ...add })
        );
      }
    },

    /** ルール全削除 */
    deleteUserRules() {
      this.rules.items = this.rules.items.filter((e) => e.isDefaultRule);
    },

    /** ルール削除 */
    deleteRow(item: PacketFilterEntity) {
      // PKよりindexを探す
      const index = this.rules.items.indexOf(item);
      this.rules.items.splice(index, 1);
    },

    /** ルール変更 */
    async editPacket(item: PacketFilterEntity) {
      if (item.isDefaultRule) {
        return;
      }
      this.uploadAlert = null;
      const edit: PacketFilterEntity = await this.$modal.show(
        PacketFilteringRuleEdit,
        {
          packetData: item,
        }
      );
      const index = this.rules.items.indexOf(item);
      this.rules.items[index] = edit;
    },
    /** 設定反映 */
    async modifyPacket() {
      this.uploadAlert = null;
      await this.$confirm(
        `パケットフィルタリングを設定します。よろしいですか？`
      );

      const noDefaultRules = this.rules.items
        .filter((e) => !e.isDefaultRule)
        .map((e) => ({
          packetFilterRuleSeq: e.packetFilterRuleSeq,
          isDefaultRule: e.isDefaultRule,
          // デフォルトルールは除外しているのでプロトコルは必須、プロトコル番号無し
          protocol: e.protocol!,
          protocolNumber: null,
          action: e.action,
          srcAddress: e.srcAddress,
          srcPort: e.srcPort,
          dstAddress: e.dstAddress,
          dstPort: e.dstPort,
          filteringStatus: e.filteringStatus,
          description: e.description,
        }));

      if (this.wnumberAct) {
        if (this.menu === "MULTI_CLOUD_GW") {
          await this.$api.packetFilter.updateCloudPktFilter(this.wnumberAct, {
            packetFilterRuleList: noDefaultRules,
            vnConnectPacketFilterKeyId: this.vnConnectPacketFilterKeyId!,
          });
          (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
        } else if (this.menu === "SASE_GATEWAY") {
          await this.$api.packetFilter.updateSaseGatewayPktFilter(
            this.wnumberAct,
            {
              packetFilterRuleList: noDefaultRules,
              saseGatewayPacketFilterKeyId: this.saseGatewayPacketFilterKeyId!,
            }
          );
          (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
        } else {
          await this.$api.packetFilter.updateVnConnectPktFilter(
            this.wnumberAct,
            {
              packetFilterRuleList: noDefaultRules,
              vnConnectPacketFilterKeyId: this.vnConnectPacketFilterKeyId!,
            }
          );
          (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
        }
      } else {
        await this.$api.packetFilter.updateAcLinePktFilter(this.enumber!, {
          packetFilterRuleList: noDefaultRules,
          accessLinePacketFilterKeyId: this.accessLinePacketFilterKeyId!,
        });
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
      }
    },

    /** tdにクラスを追加したい場合、ここで */
    cellClass(
      field: AppDraggableTableHeader<PacketFilterEntity>
    ): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },

    /** ルール変更不可・可能 */
    /**
     * trにクラスを追加したい場合、ここで
     * 共通クラス：
     * draggable: ドラッグ可能
     * clickable: 行クリック可能
     * gray-out : 行灰色
     */
    rowClass(item: TableItem): string[] | undefined {
      switch (item.isDefaultRule) {
        case true:
          // ドラッグ不可
          return undefined;
        case false:
          // ドラッグ可能
          return ["draggable", "clickable"];
      }
    },
  },
});
</script>
